import {
  Outlet,
  Link,
  createRootRouteWithContext,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import appCss from "../styles.css?url";
import interFontUrl from "@/assets/fonts/inter-variable.woff2?url";
import cormorantFontUrl from "@/assets/fonts/cormorant-garamond.woff2?url";
import { CartProvider } from "@/lib/cart";
import { AuthProvider } from "@/lib/auth";
import { WishlistProvider } from "@/lib/wishlist";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Toaster } from "@/components/ui/sonner";
import { CookieConsent } from "@/components/site/CookieConsent";
import { organizationJsonLd, websiteJsonLd } from "@/lib/seo";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <div className="eyebrow mb-6">Error 404</div>
        <h1 className="font-serif text-5xl text-foreground">Page not found</h1>
        <p className="mt-4 text-sm text-muted-foreground">
          The page you're looking for has moved, or never existed.
        </p>
        <div className="mt-8">
          <Link
            to="/"
            className="btn-gold inline-flex items-center justify-center px-8 py-3 text-xs uppercase tracking-[0.24em]"
          >
            Return Home
          </Link>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "theme-color", content: "#f5f1ea" },
      { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1" },
      { name: "googlebot", content: "index, follow, max-image-preview:large, max-snippet:-1" },
      { name: "google-site-verification", content: "FhKfMUrMtmHB1lfdyE-4JKEeT_lR3SMOIUpVPUOElKM" },
      { title: "KAKAIYS — Handcrafted Luxury Leather Jackets & Bags" },
      {
        name: "description",
        content:
          "Handcrafted full-grain leather jackets, bags, wallets and belts — finished by hand in Sialkot. Free worldwide shipping.",
      },
      { property: "og:site_name", content: "KAKAIYS" },
      { property: "og:title", content: "KAKAIYS — Handcrafted Luxury Leather Jackets & Bags" },
      {
        property: "og:description",
        content:
          "Handcrafted full-grain leather jackets, bags, wallets and belts — finished by hand in Sialkot.",
      },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "en_US" },
      { property: "og:image", content: "https://kakaiys.com/og-image.jpg" },
      { property: "og:image:type", content: "image/jpeg" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: "KAKAIYS — Handcrafted luxury leather goods" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "KAKAIYS — Handcrafted Luxury Leather Jackets & Bags" },
      {
        name: "twitter:description",
        content:
          "Handcrafted full-grain leather jackets, bags, wallets and belts — finished by hand in Sialkot.",
      },
      { name: "twitter:image", content: "https://kakaiys.com/og-image.jpg" },
    ],
    links: [
      { rel: "icon", href: "/favicon.ico", sizes: "any" },
      { rel: "icon", type: "image/svg+xml", href: "/favicon.svg" },
      { rel: "icon", type: "image/png", sizes: "16x16", href: "/favicon-16.png" },
      { rel: "icon", type: "image/png", sizes: "32x32", href: "/favicon-32.png" },
      { rel: "icon", type: "image/png", sizes: "48x48", href: "/favicon-48.png" },
      { rel: "icon", type: "image/png", sizes: "192x192", href: "/favicon-192.png" },
      { rel: "icon", type: "image/png", sizes: "512x512", href: "/favicon-512.png" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png" },
      { rel: "mask-icon", href: "/favicon.svg", color: "#14100c" },
      { rel: "manifest", href: "/site.webmanifest" },
      { rel: "stylesheet", href: appCss },
      // Canonical is set per-route on leaves to avoid duplicate <link rel="canonical"> on every page (TanStack/router#6719 — links concatenate, don't dedupe).
      // Self-hosted fonts — preloaded for fast first paint, no third-party round trips
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: interFontUrl,
        crossOrigin: "anonymous",
      },
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: cormorantFontUrl,
        crossOrigin: "anonymous",
      },
      // dns-prefetch only — full preconnect was flagged as unused on homepage (Supabase is not requested above the fold).
      // Keeps name resolution warm for routes that DO call Supabase (auth, cart, wishlist, product) without spending a TLS handshake.
      { rel: "dns-prefetch", href: "https://tvmsfjwzfnidomctinqz.supabase.co" },
      // Discovery hint for AI crawlers and humans
      { rel: "alternate", type: "text/plain", href: "https://kakaiys.com/llms.txt", title: "LLM index" },
    ],
    scripts: [
      {
        children:
          // Google Analytics (gtag.js) loads EAGERLY so Google Tag Assistant and
          // GSC's tag-detection probe can see it on first byte. It's ~50KB async
          // and runs off the main thread — minimal LCP/INP impact.
          // Meta Pixel stays DEFERRED (interaction / idle / 3s fallback) since
          // it's heavier and not required for SEO/GSC.
          // Consent stays denied-by-default until user grants via CookieConsent.
          "(function(){window.dataLayer=window.dataLayer||[];window.gtag=window.gtag||function(){window.dataLayer.push(arguments);};gtag('js',new Date());gtag('consent','default',{ad_storage:'denied',analytics_storage:'denied',ad_user_data:'denied',ad_personalization:'denied'});try{var c=localStorage.getItem('kakaiys.consent.v1');if(c==='granted'){gtag('consent','update',{ad_storage:'granted',analytics_storage:'granted',ad_user_data:'granted',ad_personalization:'granted'});}}catch(e){}var g=document.createElement('script');g.async=true;g.src='https://www.googletagmanager.com/gtag/js?id=G-F0QPK6JTGP';document.head.appendChild(g);gtag('config','G-F0QPK6JTGP');window.fbq=window.fbq||function(){(window.fbq.q=window.fbq.q||[]).push(arguments);};window.fbq.l=+new Date();var fbLoaded=false;function loadFb(){if(fbLoaded)return;fbLoaded=true;!function(f,b,e,v,n,t,s){if(f._fbqLoaded)return;f._fbqLoaded=1;n=f.fbq;t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s);n.push=n;n.loaded=!0;n.version='2.0';n.queue=n.q||[];}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');try{var c2=localStorage.getItem('kakaiys.consent.v1');if(c2==='granted'){fbq('consent','grant');}else{fbq('consent','revoke');}}catch(e){}fbq('init','1501500044957957');fbq('track','PageView');}var events=['pointerdown','keydown','touchstart','scroll'];function trigger(){events.forEach(function(ev){window.removeEventListener(ev,trigger,{passive:true});});loadFb();}events.forEach(function(ev){window.addEventListener(ev,trigger,{passive:true,once:true});});if('requestIdleCallback' in window){requestIdleCallback(function(){setTimeout(loadFb,1500);},{timeout:3000});}else{setTimeout(loadFb,3000);}})();",
      },
      { type: "application/ld+json", children: organizationJsonLd() },
      { type: "application/ld+json", children: websiteJsonLd() },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <WishlistProvider>
          <CartProvider>
            <a
              href="#main-content"
              className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100] focus:bg-foreground focus:text-background focus:px-4 focus:py-2 focus:text-xs focus:uppercase focus:tracking-[0.24em]"
            >
              Skip to main content
            </a>
            <Header />
            <main id="main-content">
              <Outlet />
            </main>
            <Footer />
            <CookieConsent />
            <Toaster />
          </CartProvider>
        </WishlistProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}
